/**
 * Facets Accordion
 *
 * Manages open/closed state of <details class="facets__disclosure--accordion">
 * filter groups, persisted per session in sessionStorage so that user choices
 * survive filter re-renders and page reloads within the same session.
 *
 * Each filter group is keyed by a stable composite of its filter param and
 * its sidebar/drawer location (read from the element's id), so the desktop
 * sidebar and the mobile drawer remember their state independently.
 *
 * Also re-initializes the theme's <show-more> component the first time an
 * accordion opens, since show-more measures scrollHeight on construction and
 * a closed <details> reports 0 — without re-init, the "show more" toggle
 * would never appear even on long lists.
 */

(function () {
  if (window.__facetsAccordionInit) return;
  window.__facetsAccordionInit = true;

  var STORAGE_KEY = 'trws:facets-accordion-open';

  function readState() {
    try {
      var raw = window.sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return {};
      var parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) {
      return {};
    }
  }

  function writeState(state) {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      /* sessionStorage unavailable (private mode etc.) — silently ignore */
    }
  }

  // Use the element's id (e.g. "Filter-filter.v.option.color-sidebar") as the
  // storage key. It already encodes both the filter param and the location.
  function keyFor(detailsEl) {
    return detailsEl.id || '';
  }

  function applyStoredState(detailsEl, state) {
    var key = keyFor(detailsEl);
    if (!key) return;
    if (Object.prototype.hasOwnProperty.call(state, key)) {
      if (state[key]) {
        detailsEl.setAttribute('open', '');
      } else {
        detailsEl.removeAttribute('open');
      }
    }
    // No stored value -> leave as authored (closed by default).
  }

  function reinitShowMore(detailsEl) {
    // The theme's <show-more> custom element measures scrollHeight in its
    // constructor. When it was constructed inside a closed <details>, that
    // height was 0, so the "show more" toggle is hidden. Re-running its
    // init() once the panel is visible fixes the calculation.
    if (detailsEl.dataset.showMoreReinit === 'true') return;
    var showMoreEl = detailsEl.querySelector('show-more');
    if (showMoreEl && typeof showMoreEl.init === 'function') {
      try {
        showMoreEl.init();
      } catch (e) {
        /* fail open — leave show-more in its existing state */
      }
    }
    detailsEl.dataset.showMoreReinit = 'true';
  }

  function onToggle(event) {
    var detailsEl = event.target;
    if (!(detailsEl instanceof HTMLElement)) return;
    if (!detailsEl.classList.contains('facets__disclosure--accordion')) return;

    var state = readState();
    var key = keyFor(detailsEl);
    if (!key) return;

    if (detailsEl.open) {
      state[key] = true;
      reinitShowMore(detailsEl);
    } else {
      state[key] = false;
    }
    writeState(state);
  }

  function bindAll(root) {
    var scope = root || document;
    var details = scope.querySelectorAll('details.facets__disclosure--accordion');
    if (!details.length) return;

    var state = readState();
    details.forEach(function (el) {
      applyStoredState(el, state);

      // The toggle event doesn't bubble, so we bind per element. Using a
      // marker prevents double-binding on filter re-render (which replaces
      // innerHTML but keeps the outer <details> node).
      if (el.dataset.accordionBound === 'true') return;
      el.dataset.accordionBound = 'true';
      el.addEventListener('toggle', onToggle);

      // If a stored-open accordion contains a show-more, re-init it now
      // (e.g. after page refresh where the details starts open).
      if (el.open) {
        // Defer to allow show-more's own constructor to run first.
        window.requestAnimationFrame(function () {
          reinitShowMore(el);
        });
      }
    });
  }

  // Watch the filters wrapper for re-renders triggered by FacetFiltersForm,
  // which replaces innerHTML inside .js-filter elements. New <details> nodes
  // need their stored state re-applied; existing ones keep [open] but may
  // need show-more re-init if their inner panel was rebuilt while open.
  function observeWrappers() {
    var wrappers = document.querySelectorAll('[id^="FacetsWrapper-"]');
    if (!wrappers.length) return;

    var observer = new MutationObserver(function (mutations) {
      var touched = false;
      mutations.forEach(function (m) {
        if (m.type === 'childList' && (m.addedNodes.length || m.removedNodes.length)) {
          touched = true;
        }
      });
      if (touched) {
        // After a re-render, clear the show-more reinit flag on any details
        // whose inner panel was rebuilt, so the next open re-measures it.
        document.querySelectorAll('details.facets__disclosure--accordion').forEach(function (el) {
          delete el.dataset.showMoreReinit;
        });
        bindAll();
      }
    });

    wrappers.forEach(function (w) {
      observer.observe(w, { childList: true, subtree: true });
    });
  }

  function init() {
    bindAll();
    observeWrappers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
